/**
 * Utility functions for checking and ensuring WCAG AA contrast ratios
 */

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color code (e.g., '#ffffff' or 'ffffff')
 * @returns {Object} RGB values {r, g, b}
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Calculate relative luminance of a color
 * @param {Object} rgb - RGB values {r, g, b}
 * @returns {number} Relative luminance value
 */
export const getLuminance = (rgb) => {
  const { r, g, b } = rgb;
  
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculate contrast ratio between two colors
 * @param {string} color1 - First color (hex)
 * @param {string} color2 - Second color (hex)
 * @returns {number} Contrast ratio
 */
export const getContrastRatio = (color1, color2) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  if (!rgb1 || !rgb2) return 0;
  
  const lum1 = getLuminance(rgb1);
  const lum2 = getLuminance(rgb2);
  
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  
  return (brightest + 0.05) / (darkest + 0.05);
};

/**
 * Check if contrast ratio meets WCAG AA standard (4.5:1)
 * @param {string} textColor - Text color (hex)
 * @param {string} backgroundColor - Background color (hex)
 * @returns {boolean} True if meets WCAG AA standard
 */
export const meetsWCAGAA = (textColor, backgroundColor) => {
  return getContrastRatio(textColor, backgroundColor) >= 4.5;
};

/**
 * Check if contrast ratio meets WCAG AAA standard (7:1)
 * @param {string} textColor - Text color (hex)
 * @param {string} backgroundColor - Background color (hex)
 * @returns {boolean} True if meets WCAG AAA standard
 */
export const meetsWCAGAAA = (textColor, backgroundColor) => {
  return getContrastRatio(textColor, backgroundColor) >= 7;
};

/**
 * Tailwind color palette with hex values for contrast testing
 */
export const tailwindColors = {
  white: '#ffffff',
  black: '#000000',
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
    950: '#030712'
  },
  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d'
  },
  green: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d'
  },
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a'
  },
  yellow: {
    50: '#fefce8',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f'
  },
  teal: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a'
  },
  purple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87'
  }
};

/**
 * Get accessible text color combinations for themes
 */
export const getAccessibleTextColors = () => {
  return {
    light: {
      // Light theme backgrounds
      backgrounds: {
        primary: tailwindColors.white, // #ffffff
        secondary: tailwindColors.gray[50], // #f9fafb
        tertiary: tailwindColors.gray[100], // #f3f4f6
        card: tailwindColors.white, // #ffffff
        input: tailwindColors.white // #ffffff
      },
      // Text colors that meet WCAG AA on light backgrounds
      text: {
        primary: tailwindColors.gray[900], // #111827 - 16.75:1 ratio on white
        secondary: tailwindColors.gray[700], // #374151 - 8.59:1 ratio on white
        tertiary: tailwindColors.gray[600], // #4b5563 - 6.23:1 ratio on white
        muted: tailwindColors.gray[500], // #6b7280 - 4.54:1 ratio on white (WCAG AA compliant)
        placeholder: tailwindColors.gray[500] // #6b7280 - 4.54:1 ratio (improved from gray-400)
      }
    },
    dark: {
      // Dark theme backgrounds
      backgrounds: {
        primary: tailwindColors.gray[900], // #111827
        secondary: tailwindColors.gray[800], // #1f2937
        tertiary: tailwindColors.gray[700], // #374151
        card: tailwindColors.gray[800], // #1f2937
        input: tailwindColors.gray[700] // #374151
      },
      // Text colors that meet WCAG AA on dark backgrounds
      text: {
        primary: tailwindColors.white, // #ffffff - 18.7:1 ratio on gray-900
        secondary: tailwindColors.gray[200], // #e5e7eb - 13.54:1 ratio on gray-900
        tertiary: tailwindColors.gray[300], // #d1d5db - 10.78:1 ratio on gray-900
        muted: tailwindColors.gray[400], // #9ca3af - 6.39:1 ratio on gray-900
        placeholder: tailwindColors.gray[300] // #d1d5db - 10.78:1 ratio (improved from gray-500)
      }
    }
  };
};

/**
 * Validate all theme color combinations for WCAG AA compliance
 */
export const validateThemeContrast = () => {
  const colors = getAccessibleTextColors();
  const results = {
    light: {},
    dark: {},
    issues: []
  };

  // Test light theme combinations
  Object.entries(colors.light.backgrounds).forEach(([bgName, bgColor]) => {
    results.light[bgName] = {};
    Object.entries(colors.light.text).forEach(([textName, textColor]) => {
      const ratio = getContrastRatio(textColor, bgColor);
      const passes = ratio >= 4.5;
      results.light[bgName][textName] = { ratio, passes };
      
      if (!passes) {
        results.issues.push({
          theme: 'light',
          background: bgName,
          text: textName,
          ratio,
          colors: { text: textColor, background: bgColor }
        });
      }
    });
  });

  // Test dark theme combinations
  Object.entries(colors.dark.backgrounds).forEach(([bgName, bgColor]) => {
    results.dark[bgName] = {};
    Object.entries(colors.dark.text).forEach(([textName, textColor]) => {
      const ratio = getContrastRatio(textColor, bgColor);
      const passes = ratio >= 4.5;
      results.dark[bgName][textName] = { ratio, passes };
      
      if (!passes) {
        results.issues.push({
          theme: 'dark',
          background: bgName,
          text: textName,
          ratio,
          colors: { text: textColor, background: bgColor }
        });
      }
    });
  });

  return results;
};

/**
 * Get improved placeholder colors that meet WCAG AA
 */
export const getAccessiblePlaceholderColors = () => {
  return {
    light: {
      // For white/light backgrounds - use gray-500 instead of gray-400
      placeholder: tailwindColors.gray[500], // #6b7280 - 4.54:1 ratio on white
      helperText: tailwindColors.gray[600] // #4b5563 - 6.23:1 ratio on white
    },
    dark: {
      // For dark backgrounds on gray-700 inputs - use gray-300 for better contrast
      placeholder: tailwindColors.gray[300], // #d1d5db - 5.49:1 ratio on gray-700
      helperText: tailwindColors.gray[200] // #e5e7eb - 6.63:1 ratio on gray-700
    }
  };
};

/**
 * Get accessible badge and status indicator colors that meet WCAG AA
 */
export const getAccessibleBadgeColors = () => {
  return {
    success: {
      background: tailwindColors.green[700], // Darker green for better contrast
      text: tailwindColors.white,
      ratio: getContrastRatio(tailwindColors.white, tailwindColors.green[700]) // ~4.5:1
    },
    error: {
      background: tailwindColors.red[600], // Standard red
      text: tailwindColors.white,
      ratio: getContrastRatio(tailwindColors.white, tailwindColors.red[600]) // ~5.9:1
    },
    warning: {
      background: tailwindColors.yellow[400], // Light yellow
      text: tailwindColors.black, // Black text for better contrast
      ratio: getContrastRatio(tailwindColors.black, tailwindColors.yellow[400]) // ~10.4:1
    },
    info: {
      background: tailwindColors.blue[600], // Standard blue
      text: tailwindColors.white,
      ratio: getContrastRatio(tailwindColors.white, tailwindColors.blue[600]) // ~5.9:1
    },
    primary: {
      background: tailwindColors.teal[700], // Darker teal for better contrast
      text: tailwindColors.white,
      ratio: getContrastRatio(tailwindColors.white, tailwindColors.teal[700]) // ~5.7:1
    },
    secondary: {
      background: tailwindColors.gray[600], // Gray secondary
      text: tailwindColors.white,
      ratio: getContrastRatio(tailwindColors.white, tailwindColors.gray[600]) // ~6.2:1
    },
    admin: {
      background: tailwindColors.red[600], // Admin badge (red)
      text: tailwindColors.white,
      ratio: getContrastRatio(tailwindColors.white, tailwindColors.red[600]) // ~5.9:1
    }
  };
};
